// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useCallback, useState } from 'react';

import type { HexTriplet } from '@evolvemep/api-ts';
import type { MantineColor } from '@mantine/core';

import { workOrderStateColorMap, taskStatusColorMap, shipmentStateColorMap } from 'constants/badgeMappingStatus';
import { isNotNil } from 'helpers/isNotNil';
import { strongFromEntries, strongEntries, strongKeys } from 'helpers/strongEntries';
import { useShipmentStatuses } from 'hooks/typeCacheHooks/useShipmentStatuses';
import { useTaskStatuses } from 'hooks/typeCacheHooks/useTaskStatuses';
import { useWorkOrderStatuses } from 'hooks/typeCacheHooks/useWorkOrderStatuses';

import type { CustomStatusEntityName, CustomStatusesHookValue, StateNameFor, StatusIdFor } from './types';

/**
 * Canonical-state name → color (and label, if label does not match the state name).
 *
 * The order of the keys in the map determines the render order in components.
 * */
export type BaseStateColorMap<T extends CustomStatusEntityName> = Record<
  StateNameFor<T>,
  | MantineColor
  | {
      color: MantineColor;
      label: string;
    }
>;

type CustomStatusEntityRegistryEntry<TEntity extends CustomStatusEntityName> = {
  /** Plural form for user-facing copy (e.g., "Work Orders", "Shipments", etc). */
  pluralName: string;
  /** Map of state → color + label */
  stateColorMap: BaseStateColorMap<TEntity>;
  /** If set, only these state names support custom statuses; otherwise every state does. */
  statesWithStatuses?: readonly StateNameFor<TEntity>[];
  /** Entity-specific hook for interacting with custom statuses. */
  useStatuses: () => CustomStatusesHookValue<TEntity>;
};

/**
 * Single source of truth for adding a custom-status-supporting entity to the UI. Adding a
 * new entity = adding an entry to `CustomStatusEntityIdMap` (in `./types`), its hook, and a
 * row here. The shared status components read everything they need from this registry.
 *
 * Typed via a mapped type so per-entity access (e.g. via `useCustomStatuses`)
 * keeps the entity's narrow ID types.
 */
const customStatusEntityConfig: { [TEntity in CustomStatusEntityName]: CustomStatusEntityRegistryEntry<TEntity> } = {
  'Work Order': {
    pluralName: 'Work Orders',
    stateColorMap: workOrderStateColorMap,
    statesWithStatuses: ['Draft', 'Pending', 'Approved', 'Not Started', 'In Progress', 'Blocked', 'Completed'],
    useStatuses: useWorkOrderStatuses,
  },
  'Shipment': {
    pluralName: 'Shipments',
    stateColorMap: shipmentStateColorMap,
    useStatuses: useShipmentStatuses,
  },
  'Task': {
    pluralName: 'Tasks',
    stateColorMap: taskStatusColorMap,
    // Tasks do not yet support custom statuses
    statesWithStatuses: [],
    useStatuses: useTaskStatuses,
  },
};

export type StateColorMap<T extends CustomStatusEntityName> = Record<
  StateNameFor<T>,
  { label: string; color: MantineColor }
>;

/**
 * Used to interact with a custom-status-supporting entity's states and statuses.
 *
 * @param customStatusEntityName The name of the entity for which custom statuses are being managed. This must NEVER change during render.
 */
export const useCustomStatuses = <TEntity extends CustomStatusEntityName>(customStatusEntityName: TEntity) => {
  // A change in `customStatusEntityName` would cause different hooks to render, breaking the page.
  // Note: `customStatusEntityName` should NEVER change while rendering; this is just a safety net.
  const [name] = useState(customStatusEntityName);
  const {
    useStatuses,
    stateColorMap: initialStateColorMap,
    statesWithStatuses: initialStatesWithStatuses,
    ...rest
  } = customStatusEntityConfig[name];
  const { statusMap, states: unsortedStates, ...context } = useStatuses();

  // The order of the keys in the color map determines the render order of the states
  const stateOrder = useMemo(() => strongKeys(initialStateColorMap), [initialStateColorMap]);

  /** The list of all states for `TEntity` */
  const states = useMemo(
    () =>
      unsortedStates
        // Sometimes, new states get added on the backend before the frontend supports them.
        // This will filter them out to prevent errors.
        ?.filter((s) => stateOrder.includes(s.name))
        .toSorted((a, b) => stateOrder.indexOf(a.name) - stateOrder.indexOf(b.name)),
    [stateOrder, unsortedStates],
  );

  /** The list of states that support custom statuses. */
  const statesWithStatuses = useMemo<readonly StateNameFor<TEntity>[]>(
    () =>
      (initialStatesWithStatuses ?? states?.map((s) => s.name))?.toSorted(
        (a, b) => stateOrder.indexOf(a) - stateOrder.indexOf(b),
      ) ?? [],
    [initialStatesWithStatuses, states, stateOrder],
  );

  /** Map of state name to its color and display label */
  const stateColorMap = useMemo<StateColorMap<TEntity>>(
    () =>
      strongFromEntries(
        strongEntries(initialStateColorMap).map(
          ([stateName, labelAndColor]): [StateNameFor<TEntity>, { label: string; color: MantineColor }] => {
            if (typeof labelAndColor === 'object') {
              return [stateName, labelAndColor] as const;
            }
            return [stateName, { label: stateName, color: labelAndColor }] as const;
          },
        ),
      ),
    [initialStateColorMap],
  );

  /** Get the label and color for a given state/status */
  const getLabelAndColor = useCallback(
    (stateName: StateNameFor<TEntity>, statusId?: StatusIdFor<TEntity> | null) => {
      const status = isNotNil(statusId) && statusId in statusMap ? statusMap[statusId] : null;
      const { label: stateLabel, color: stateColor } = stateColorMap[stateName];
      const label = status?.name ?? stateLabel;
      const color = status?.color ?? stateColor;
      const tooltip = label === stateLabel ? label : `${stateLabel}: ${label}`;
      return { label, color, tooltip } as const as {
        label: string;
        color: MantineColor | HexTriplet;
        tooltip: string;
      };
    },
    [stateColorMap, statusMap],
  );

  return {
    ...context,
    ...rest,
    states,
    getLabelAndColor,
    stateColorMap,
    statusMap,
    statesWithStatuses,
  };
};
